// slices/adminSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import AdminService from "../services/AdminService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ✅ ADMIN-SPECIFIC TOKEN KEYS (to avoid conflict with provider tokens)
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'admin_access_token',
  REFRESH_TOKEN: 'admin_refresh_token',
  USER_DATA: 'admin_user_data',
  TOKEN_EXPIRY: 'admin_token_expiry',
  USER_TYPE: 'admin_user_type',
};

// ==================== HELPER: Clear Admin Tokens ====================
const clearAdminTokens = () => {
  try {
    Object.values(TOKEN_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('✅ Admin tokens cleared successfully');
    return true;
  } catch (error) {
    console.error('❌ Failed to clear admin tokens:', error);
    return false;
  }
};

// ==================== HELPER: Clear All Admin Data ====================
const clearAllAdminData = () => {
  clearAdminTokens();
  
  // Also clear admin-specific cache if any
  const cacheKeys = [
    'admin_list_cache',
    'admin_detail_cache',
    'admin_stats_cache',
    'admin_activity_cache',
    'admin_provider_list_cache',
    'admin_provider_detail_cache'
  ];
  
  try {
    cacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('✅ Admin cache cleared successfully');
  } catch (error) {
    console.error('❌ Failed to clear admin cache:', error);
  }
};

// ==================== ASYNC THUNKS ====================
export const adminLogin = createAsyncThunk(
  'admin/adminLogin',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await AdminService.login(email, password);
      
      if (!response.success) {
        return rejectWithValue(response.message || 'Login failed');
      }
      
      // AdminService.login already stores tokens in localStorage
      // using the same keys that api.js expects
      
      console.log('✅ Admin login successful');
      return response;
    } catch (error) {
      console.error('❌ Admin login error:', error);
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

/**
 * Logout admin - ✅ CLEARS ALL ADMIN TOKENS
 * POST /api/admin/logout
 */
export const adminLogout = createAsyncThunk(
  'admin/adminLogout',
  async (_, { rejectWithValue }) => {
    try {
      // Call logout API
      await AdminService.logout();
      
      // ✅ Clear all admin tokens from localStorage
      clearAllAdminData();
      
      return { success: true };
    } catch (error) {
      // Even if API call fails, clear local tokens
      clearAllAdminData();
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Upload admin profile image
 * POST /api/admin/:id/profile-image
 */
export const adminUploadProfileImage = createAsyncThunk(
  'admin/adminUploadProfileImage',
  async ({ id, file }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!file) {
        return rejectWithValue('Image file is required');
      }
      const response = await AdminService.uploadProfileImage(id, file);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to upload profile image');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Remove admin profile image
 * DELETE /api/admin/:id/profile-image
 */
export const adminRemoveProfileImage = createAsyncThunk(
  'admin/adminRemoveProfileImage',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.removeProfileImage(id);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to remove profile image');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update admin basic information (name, phone, language)
 * PUT /api/admin/:id/basic-info
 */
export const adminUpdateBasicInfo = createAsyncThunk(
  'admin/adminUpdateBasicInfo',
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!updateData) {
        return rejectWithValue('Update data is required');
      }
      const response = await AdminService.updateAdminBasicInfo(id, updateData);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update basic information');
      }
      return { ...response, id, updateData };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update admin email
 * PUT /api/admin/:id/email
 */
export const adminUpdateEmail = createAsyncThunk(
  'admin/adminUpdateEmail',
  async ({ id, newEmail, currentPassword }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!newEmail) {
        return rejectWithValue('New email is required');
      }
      if (!currentPassword) {
        return rejectWithValue('Current password is required');
      }
      const response = await AdminService.updateAdminEmail(id, newEmail, currentPassword);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update email');
      }
      return { ...response, id, newEmail };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update admin password
 * PUT /api/admin/:id/password
 */
export const adminUpdatePassword = createAsyncThunk(
  'admin/adminUpdatePassword',
  async ({ id, currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!currentPassword) {
        return rejectWithValue('Current password is required');
      }
      if (!newPassword) {
        return rejectWithValue('New password is required');
      }
      if (newPassword.length < 8) {
        return rejectWithValue('New password must be at least 8 characters long');
      }
      const response = await AdminService.updateAdminPassword(id, currentPassword, newPassword);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update password');
      }
      return { ...response, id };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update admin full profile (multiple fields at once)
 * PUT /api/admin/:id/full-profile
 */
export const adminUpdateFullProfile = createAsyncThunk(
  'admin/adminUpdateFullProfile',
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!updateData) {
        return rejectWithValue('Update data is required');
      }
      const response = await AdminService.updateAdminFullProfile(id, updateData);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update full profile');
      }
      return { ...response, id, updateData };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update admin two-factor authentication settings
 * PUT /api/admin/:id/two-factor-settings
 */
export const adminUpdateTwoFactorAuth = createAsyncThunk(
  'admin/adminUpdateTwoFactorAuth',
  async ({ id, enabled, secret = null }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (enabled === undefined || enabled === null) {
        return rejectWithValue('Enabled status is required');
      }
      const response = await AdminService.updateAdminTwoFactorAuth(id, enabled, secret);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update two-factor authentication');
      }
      return { ...response, id, enabled };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update admin notification preferences
 * PUT /api/admin/:id/notifications
 */
export const adminUpdateNotificationPreferences = createAsyncThunk(
  'admin/adminUpdateNotificationPreferences',
  async ({ id, preferences }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!preferences) {
        return rejectWithValue('Notification preferences are required');
      }
      const response = await AdminService.updateNotificationPreferences(id, preferences);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update notification preferences');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get admin notification preferences
 * GET /api/admin/:id/notifications
 */
export const adminGetNotificationPreferences = createAsyncThunk(
  'admin/adminGetNotificationPreferences',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.getNotificationPreferences(id);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch notification preferences');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update dashboard settings
 * PUT /api/admin/:id/dashboard
 */
export const adminUpdateDashboardSettings = createAsyncThunk(
  'admin/adminUpdateDashboardSettings',
  async ({ id, settings }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!settings) {
        return rejectWithValue('Dashboard settings are required');
      }
      const response = await AdminService.updateDashboardSettings(id, settings);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update dashboard settings');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get dashboard settings
 * GET /api/admin/:id/dashboard
 */
export const adminGetDashboardSettings = createAsyncThunk(
  'admin/adminGetDashboardSettings',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.getDashboardSettings(id);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch dashboard settings');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Enable/Disable two-factor authentication
 * PATCH /api/admin/:id/two-factor
 */
export const adminToggleTwoFactorAuth = createAsyncThunk(
  'admin/adminToggleTwoFactorAuth',
  async ({ id, enabled }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (enabled === undefined || enabled === null) {
        return rejectWithValue('Enabled status is required');
      }
      const response = await AdminService.toggleTwoFactorAuth(id, enabled);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to toggle two-factor authentication');
      }
      return { ...response, id, enabled };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Check if admin has specific permission
 * GET /api/admin/:id/permissions/:permission
 */
export const adminCheckPermission = createAsyncThunk(
  'admin/adminCheckPermission',
  async ({ id, permission }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!permission) {
        return rejectWithValue('Permission is required');
      }
      const response = await AdminService.checkPermission(id, permission);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to check permission');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get all available permissions
 * GET /api/admin-permissions
 */
export const adminGetPermissions = createAsyncThunk(
  'admin/adminGetPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminPermissions();
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch permissions');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Bulk update admins
 * PATCH /api/admins/bulk
 */
export const adminBulkUpdateAdmins = createAsyncThunk(
  'admin/adminBulkUpdateAdmins',
  async ({ ids, updateData }, { rejectWithValue }) => {
    try {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        return rejectWithValue('Admin IDs array is required');
      }
      if (!updateData) {
        return rejectWithValue('Update data is required');
      }
      const response = await AdminService.bulkUpdateAdmins(ids, updateData);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to bulk update admins');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Bulk delete admins
 * DELETE /api/admins/bulk
 */
export const adminBulkDeleteAdmins = createAsyncThunk(
  'admin/adminBulkDeleteAdmins',
  async (ids, { rejectWithValue }) => {
    try {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        return rejectWithValue('Admin IDs array is required');
      }
      const response = await AdminService.bulkDeleteAdmins(ids);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to bulk delete admins');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get admin roles (only admin role available)
 * GET /api/admin-roles
 */
export const adminGetRoles = createAsyncThunk(
  'admin/adminGetRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminRoles();
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch admin roles');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get admin statuses (for dropdowns)
 * GET /api/admin-statuses
 */
export const adminGetStatuses = createAsyncThunk(
  'admin/adminGetStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminStatuses();
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch admin statuses');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);


/**
 * Refresh access token
 * POST /api/admin/refresh-token
 */
export const adminRefreshToken = createAsyncThunk(
  'admin/adminRefreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminService.refreshToken();
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to refresh token');
      }
      
      // ✅ Update tokens in localStorage
      const { accessToken, refreshToken } = response.data || {};
      
      if (accessToken) {
        localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      }
      if (refreshToken) {
        localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
      }
      localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
      
      return response;
    } catch (error) {
      // If refresh fails, clear tokens
      clearAdminTokens();
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Request password reset
 * POST /api/admin/password-reset/request
 */
export const adminRequestPasswordReset = createAsyncThunk(
  'admin/adminRequestPasswordReset',
  async (email, { rejectWithValue }) => {
    try {
      if (!email) {
        return rejectWithValue('Email is required');
      }
      const response = await AdminService.requestPasswordReset(email);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to request password reset');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Reset password using token
 * POST /api/admin/password-reset/reset
 */
export const adminResetPassword = createAsyncThunk(
  'admin/adminResetPassword',
  async ({ token, newPassword, confirmPassword }, { rejectWithValue }) => {
    try {
      if (!token) {
        return rejectWithValue('Reset token is required');
      }
      if (!newPassword) {
        return rejectWithValue('New password is required');
      }
      if (!confirmPassword) {
        return rejectWithValue('Confirm password is required');
      }
      if (newPassword !== confirmPassword) {
        return rejectWithValue('Passwords do not match');
      }
      if (newPassword.length < 8) {
        return rejectWithValue('New password must be at least 8 characters long');
      }
      const response = await AdminService.resetPassword(token, newPassword, confirmPassword);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to reset password');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Verify admin email
 * POST /api/admin/verify-email
 */
export const adminVerifyEmail = createAsyncThunk(
  'admin/adminVerifyEmail',
  async (token, { rejectWithValue }) => {
    try {
      if (!token) {
        return rejectWithValue('Verification token is required');
      }
      const response = await AdminService.verifyEmail(token);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to verify email');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);



/**
 * Check authentication status - Uses localStorage directly
 */
export const adminCheckAuth = createAsyncThunk(
  'admin/adminCheckAuth',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔍 Running admin auth check...');
      
      // ✅ ADMIN-SPECIFIC TOKEN KEYS (must match api.js)
      const ADMIN_ACCESS_TOKEN = 'admin_access_token';
      const ADMIN_REFRESH_TOKEN = 'admin_refresh_token';
      const ADMIN_USER_DATA = 'admin_user_data';
      const ADMIN_TOKEN_EXPIRY = 'admin_token_expiry';
      const ADMIN_USER_TYPE = 'admin_user_type';
      
      // Check for admin-specific tokens
      const accessToken = localStorage.getItem(ADMIN_ACCESS_TOKEN);
      const userType = localStorage.getItem(ADMIN_USER_TYPE);
      const userDataStr = localStorage.getItem(ADMIN_USER_DATA);
      
      console.log('🔍 Admin tokens found:', {
        hasAccessToken: !!accessToken,
        userType: userType,
        hasUserData: !!userDataStr
      });
      
      // If no token or wrong user type, not authenticated
      if (!accessToken || userType !== 'admin' || !userDataStr) {
        console.log('❌ Admin auth check: No valid admin session found');
        // Clear any stale admin tokens
        localStorage.removeItem(ADMIN_ACCESS_TOKEN);
        localStorage.removeItem(ADMIN_REFRESH_TOKEN);
        localStorage.removeItem(ADMIN_USER_DATA);
        localStorage.removeItem(ADMIN_TOKEN_EXPIRY);
        localStorage.removeItem(ADMIN_USER_TYPE);
        
        return {
          success: true,
          data: {
            isAuthenticated: false,
            user: null
          }
        };
      }

      // Try to parse user data
      let userData;
      try {
        userData = JSON.parse(userDataStr);
      } catch (error) {
        console.error('❌ Admin auth check: Invalid user data', error);
        localStorage.removeItem(ADMIN_ACCESS_TOKEN);
        localStorage.removeItem(ADMIN_REFRESH_TOKEN);
        localStorage.removeItem(ADMIN_USER_DATA);
        localStorage.removeItem(ADMIN_TOKEN_EXPIRY);
        localStorage.removeItem(ADMIN_USER_TYPE);
        
        return {
          success: true,
          data: {
            isAuthenticated: false,
            user: null
          }
        };
      }

      // Check token expiry
      const expiry = localStorage.getItem(ADMIN_TOKEN_EXPIRY);
      if (expiry) {
        const expiryTime = parseInt(expiry, 10);
        if (Date.now() > expiryTime) {
          console.log('❌ Admin auth check: Token expired');
          localStorage.removeItem(ADMIN_ACCESS_TOKEN);
          localStorage.removeItem(ADMIN_REFRESH_TOKEN);
          localStorage.removeItem(ADMIN_USER_DATA);
          localStorage.removeItem(ADMIN_TOKEN_EXPIRY);
          localStorage.removeItem(ADMIN_USER_TYPE);
          
          return {
            success: true,
            data: {
              isAuthenticated: false,
              user: null
            }
          };
        }
      }

      console.log('✅ Admin auth check: Valid admin session found for:', userData.email || userData.name);
      return {
        success: true,
        data: {
          isAuthenticated: true,
          user: userData
        }
      };
    } catch (error) {
      console.error('❌ Admin auth check error:', error);
      
      // Clear admin tokens on error
      const ADMIN_ACCESS_TOKEN = 'admin_access_token';
      const ADMIN_REFRESH_TOKEN = 'admin_refresh_token';
      const ADMIN_USER_DATA = 'admin_user_data';
      const ADMIN_TOKEN_EXPIRY = 'admin_token_expiry';
      const ADMIN_USER_TYPE = 'admin_user_type';
      
      localStorage.removeItem(ADMIN_ACCESS_TOKEN);
      localStorage.removeItem(ADMIN_REFRESH_TOKEN);
      localStorage.removeItem(ADMIN_USER_DATA);
      localStorage.removeItem(ADMIN_TOKEN_EXPIRY);
      localStorage.removeItem(ADMIN_USER_TYPE);
      
      return rejectWithValue(handleError(error));
    }
  }
);
// ==================== ADMIN MANAGEMENT THUNKS ====================

/**
 * Get all admins with filters
 * GET /api/admins
 */
export const adminGetAllAdmins = createAsyncThunk(
  'admin/adminGetAllAdmins',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAllAdmins(params);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch admins');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get admin with full details
 * GET /api/admin/:id/full
 */
export const adminGetAdminFullDetails = createAsyncThunk(
  'admin/adminGetAdminFullDetails',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.getAdminFullDetails(id);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch admin');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Create new admin
 * POST /api/admin
 */
export const adminCreateAdmin = createAsyncThunk(
  'admin/adminCreateAdmin',
  async (adminData, { rejectWithValue }) => {
    try {
      if (!adminData) {
        return rejectWithValue('Admin data is required');
      }
      const response = await AdminService.createNewAdmin(adminData);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to create admin');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update admin profile
 * PUT /api/admin/:id/profile
 */
export const adminUpdateAdmin = createAsyncThunk(
  'admin/adminUpdateAdmin',
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!updateData) {
        return rejectWithValue('Update data is required');
      }
      const response = await AdminService.updateAdminProfile(id, updateData);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update admin');
      }
      return { ...response, id };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Delete admin
 * DELETE /api/admin/:id
 */
export const adminDeleteAdmin = createAsyncThunk(
  'admin/adminDeleteAdmin',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.deleteAdmin(id);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to delete admin');
      }
      return { ...response, id };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Suspend admin
 * POST /api/admin/:id/suspend
 */
export const adminSuspendAdmin = createAsyncThunk(
  'admin/adminSuspendAdmin',
  async ({ id, reason = '' }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.suspendAdmin(id, reason);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to suspend admin');
      }
      return { ...response, id };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Activate admin
 * POST /api/admin/:id/activate
 */
export const adminActivateAdmin = createAsyncThunk(
  'admin/adminActivateAdmin',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.activateAdmin(id);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to activate admin');
      }
      return { ...response, id };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get admin activity log
 * GET /api/admin/:id/activity
 */
export const adminGetAdminActivity = createAsyncThunk(
  'admin/adminGetAdminActivity',
  async ({ id, limit = 50, skip = 0 }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.getAdminActivityLog(id, limit, skip);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch admin activity');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Clear admin activity log
 * DELETE /api/admin/:id/activity
 */
export const adminClearAdminActivity = createAsyncThunk(
  'admin/adminClearAdminActivity',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.clearAdminActivityLog(id);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to clear admin activity');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get admin activity summary
 * GET /api/admin/:id/activity-summary
 */
export const adminGetAdminActivitySummary = createAsyncThunk(
  'admin/adminGetAdminActivitySummary',
  async ({ id, days = 30 }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.getAdminActivitySummary(id, days);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch admin activity summary');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get admin stats summary
 * GET /api/admins/stats/summary
 */
export const adminGetAdminStatsSummary = createAsyncThunk(
  'admin/adminGetAdminStatsSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminStatsSummary();
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch admin stats summary');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get admin statistics
 * GET /api/admin/:id/stats
 */
export const adminGetAdminStatistics = createAsyncThunk(
  'admin/adminGetAdminStatistics',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      const response = await AdminService.getAdminStatistics(id);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch admin statistics');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update admin statistics
 * POST /api/admin/:id/stats
 */
export const adminUpdateAdminStats = createAsyncThunk(
  'admin/adminUpdateAdminStats',
  async ({ id, stats }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Admin ID is required');
      }
      if (!stats) {
        return rejectWithValue('Statistics data is required');
      }
      const response = await AdminService.updateAdminStats(id, stats);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update admin statistics');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Auth states
  isAuthenticated: false,
  user: null,
  authLoading: false,
  authError: null,
  
  // Password reset states
  passwordResetRequested: false,
  passwordResetSuccess: false,
  passwordResetError: null,
  
  // Email verification states
  emailVerified: false,
  emailVerificationError: null,
  
  // Admin management states
  admins: [],
  currentAdmin: null,
  adminStats: null,
  adminActivity: null,
  adminLoading: false,
  adminError: null,
  adminDeleteSuccess: false,
  adminUpdateSuccess: false,
  
  // Admin statistics
  adminStatsSummary: null,
  
  // Permissions and roles
  permissions: [],
  roles: [],
  statuses: [],
  
  // Pagination for admin list
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  },
  
  // Filter/Search
  filters: {},
  searchQuery: '',
  
  // UI states
  isRefreshing: false,
  selectedAdminId: null
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Reset auth states
    resetAuthState: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.authError = null;
      state.authLoading = false;
      // ✅ Clear tokens on reset
      clearAdminTokens();
    },
    
    // Reset password reset states
    resetPasswordResetState: (state) => {
      state.passwordResetRequested = false;
      state.passwordResetSuccess = false;
      state.passwordResetError = null;
    },
    
    // Reset admin states
    resetAdminState: (state) => {
      state.admins = [];
      state.currentAdmin = null;
      state.adminStats = null;
      state.adminActivity = null;
      state.adminError = null;
      state.adminDeleteSuccess = false;
      state.adminUpdateSuccess = false;
    },
    
    // Clear errors
    clearErrors: (state) => {
      state.authError = null;
      state.adminError = null;
      state.passwordResetError = null;
      state.emailVerificationError = null;
    },
    
    // Reset update state
    resetUpdateState: (state) => {
      state.adminUpdateSuccess = false;
      state.adminError = null;
    },
    
    // Reset delete state
    resetDeleteState: (state) => {
      state.adminDeleteSuccess = false;
      state.adminError = null;
    },
    
    // Set filters
    setAdminFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearAdminFilters: (state) => {
      state.filters = {};
      state.searchQuery = '';
    },
    
    // Set search query
    setAdminSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    
    // Set pagination
    setAdminPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set refreshing
    setAdminRefreshing: (state, action) => {
      state.isRefreshing = action.payload;
    },
    
    // Select admin
    selectAdmin: (state, action) => {
      state.selectedAdminId = action.payload;
    },
    
    // Clear current admin
    clearCurrentAdmin: (state) => {
      state.currentAdmin = null;
    },
    
    // Update admin in list
    updateAdminInList: (state, action) => {
      const updatedAdmin = action.payload;
      const index = state.admins.findIndex(a => a._id === updatedAdmin._id);
      if (index !== -1) {
        state.admins[index] = updatedAdmin;
      }
    },
    
    // Remove admin from list
    removeAdminFromList: (state, action) => {
      const id = action.payload;
      state.admins = state.admins.filter(a => a._id !== id);
    },
    
    // Update user data
    updateUserData: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    
    // ✅ Force clear all admin data (for emergency logout)
    forceClearAdminData: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.authError = null;
      state.authLoading = false;
      clearAllAdminData();
    }
  },
  extraReducers: (builder) => {
    builder
     .addCase(adminCheckAuth.pending, (state) => {
    state.authLoading = true;
    state.authError = null;
    console.log('🔄 Admin auth check pending...');
  })
  .addCase(adminCheckAuth.fulfilled, (state, action) => {
    state.authLoading = false;
    const { isAuthenticated, user } = action.payload.data;
    state.isAuthenticated = isAuthenticated;
    state.user = user || null;
    state.authError = null;
    console.log('✅ Admin auth check fulfilled - isAuthenticated:', state.isAuthenticated);
  })
  .addCase(adminCheckAuth.rejected, (state, action) => {
    state.authLoading = false;
    state.isAuthenticated = false;
    state.user = null;
    state.authError = action.payload || 'Failed to check authentication';
    console.log('❌ Admin auth check rejected');
  })
  
  // ========== ADMIN LOGIN ==========
  .addCase(adminLogin.pending, (state) => {
    state.authLoading = true;
    state.authError = null;
  })
  .addCase(adminLogin.fulfilled, (state, action) => {
    state.authLoading = false;
    state.isAuthenticated = true;
    state.user = action.payload.data?.admin || null;
    state.authError = null;
    console.log('✅ Admin login fulfilled - isAuthenticated:', state.isAuthenticated);
  })
  .addCase(adminLogin.rejected, (state, action) => {
    state.authLoading = false;
    state.isAuthenticated = false;
    state.user = null;
    state.authError = action.payload || 'Login failed';
    console.log('❌ Admin login rejected');
  })
  
  // ========== ADMIN LOGOUT ==========
  .addCase(adminLogout.pending, (state) => {
    state.authLoading = true;
  })
  .addCase(adminLogout.fulfilled, (state) => {
    state.authLoading = false;
    state.isAuthenticated = false;
    state.user = null;
    state.authError = null;
    console.log('✅ Admin logout fulfilled');
  })
  .addCase(adminLogout.rejected, (state) => {
    state.authLoading = false;
    state.isAuthenticated = false;
    state.user = null;
    console.log('❌ Admin logout rejected');
  })
      // ========== ADMIN REFRESH TOKEN ==========
      .addCase(adminRefreshToken.pending, (state) => {
        state.authLoading = true;
        state.authError = null;
      })
      .addCase(adminRefreshToken.fulfilled, (state) => {
        state.authLoading = false;
        state.isAuthenticated = true;
        state.authError = null;
      })
      .addCase(adminRefreshToken.rejected, (state, action) => {
        state.authLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.authError = action.payload || 'Failed to refresh token';
        // ✅ Clear tokens on refresh failure
        clearAdminTokens();
      })
      

     
      // ========== ADMIN REQUEST PASSWORD RESET ==========
      .addCase(adminRequestPasswordReset.pending, (state) => {
        state.passwordResetRequested = false;
        state.passwordResetError = null;
      })
      .addCase(adminRequestPasswordReset.fulfilled, (state) => {
        state.passwordResetRequested = true;
        state.passwordResetError = null;
      })
      .addCase(adminRequestPasswordReset.rejected, (state, action) => {
        state.passwordResetRequested = false;
        state.passwordResetError = action.payload || 'Failed to request password reset';
      })
      
      // ========== ADMIN RESET PASSWORD ==========
      .addCase(adminResetPassword.pending, (state) => {
        state.passwordResetSuccess = false;
        state.passwordResetError = null;
      })
      .addCase(adminResetPassword.fulfilled, (state) => {
        state.passwordResetSuccess = true;
        state.passwordResetError = null;
      })
      .addCase(adminResetPassword.rejected, (state, action) => {
        state.passwordResetSuccess = false;
        state.passwordResetError = action.payload || 'Failed to reset password';
      })
      
      // ========== ADMIN VERIFY EMAIL ==========
      .addCase(adminVerifyEmail.pending, (state) => {
        state.emailVerified = false;
        state.emailVerificationError = null;
      })
      .addCase(adminVerifyEmail.fulfilled, (state) => {
        state.emailVerified = true;
        state.emailVerificationError = null;
        if (state.user) {
          state.user.emailVerified = true;
        }
      })
      .addCase(adminVerifyEmail.rejected, (state, action) => {
        state.emailVerified = false;
        state.emailVerificationError = action.payload || 'Failed to verify email';
      })
      
      // ========== ADMIN GET ALL ADMINS ==========
      .addCase(adminGetAllAdmins.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.isRefreshing = true;
      })
      .addCase(adminGetAllAdmins.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.isRefreshing = false;
        state.admins = action.payload.data?.items || action.payload.data || [];
        state.pagination = action.payload.data?.pagination || initialState.pagination;
      })
      .addCase(adminGetAllAdmins.rejected, (state, action) => {
        state.adminLoading = false;
        state.isRefreshing = false;
        state.adminError = action.payload || 'Failed to fetch admins';
        // ✅ Clear tokens on 401
        if (action.payload?.statusCode === 401) {
          clearAdminTokens();
          state.isAuthenticated = false;
          state.user = null;
        }
      })
      
      // ========== ADMIN GET ADMIN FULL DETAILS ==========
      .addCase(adminGetAdminFullDetails.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(adminGetAdminFullDetails.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.currentAdmin = action.payload.data || null;
      })
      .addCase(adminGetAdminFullDetails.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to fetch admin';
      })
      
      // ========== ADMIN CREATE ADMIN ==========
      .addCase(adminCreateAdmin.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.adminUpdateSuccess = false;
      })
      .addCase(adminCreateAdmin.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminUpdateSuccess = true;
        if (action.payload.data) {
          state.admins.unshift(action.payload.data);
        }
      })
      .addCase(adminCreateAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to create admin';
        state.adminUpdateSuccess = false;
      })
      
      // ========== ADMIN UPDATE ADMIN ==========
      .addCase(adminUpdateAdmin.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.adminUpdateSuccess = false;
      })
      .addCase(adminUpdateAdmin.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminUpdateSuccess = true;
        const { id, data } = action.payload;
        const index = state.admins.findIndex(a => a._id === id || a.id === id);
        if (index !== -1 && data) {
          state.admins[index] = { ...state.admins[index], ...data };
        }
        if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
          state.currentAdmin = { ...state.currentAdmin, ...data };
        }
        if (state.user && (state.user._id === id || state.user.id === id)) {
          state.user = { ...state.user, ...data };
        }
      })
      .addCase(adminUpdateAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to update admin';
        state.adminUpdateSuccess = false;
      })
      
      // ========== ADMIN DELETE ADMIN ==========
      .addCase(adminDeleteAdmin.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.adminDeleteSuccess = false;
      })
      .addCase(adminDeleteAdmin.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminDeleteSuccess = true;
        const id = action.payload.id;
        state.admins = state.admins.filter(a => a._id !== id && a.id !== id);
        if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
          state.currentAdmin = null;
        }
      })
      .addCase(adminDeleteAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to delete admin';
        state.adminDeleteSuccess = false;
      })
      
      // ========== ADMIN SUSPEND ADMIN ==========
      .addCase(adminSuspendAdmin.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.adminUpdateSuccess = false;
      })
      .addCase(adminSuspendAdmin.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminUpdateSuccess = true;
        const id = action.payload.id;
        const index = state.admins.findIndex(a => a._id === id || a.id === id);
        if (index !== -1) {
          state.admins[index] = { ...state.admins[index], accountStatus: 'suspended' };
        }
        if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
          state.currentAdmin = { ...state.currentAdmin, accountStatus: 'suspended' };
        }
        if (state.user && (state.user._id === id || state.user.id === id)) {
          state.user = { ...state.user, accountStatus: 'suspended' };
        }
      })
      .addCase(adminSuspendAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to suspend admin';
        state.adminUpdateSuccess = false;
      })
      
      // ========== ADMIN ACTIVATE ADMIN ==========
      .addCase(adminActivateAdmin.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.adminUpdateSuccess = false;
      })
      .addCase(adminActivateAdmin.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminUpdateSuccess = true;
        const id = action.payload.id;
        const index = state.admins.findIndex(a => a._id === id || a.id === id);
        if (index !== -1) {
          state.admins[index] = { ...state.admins[index], accountStatus: 'active' };
        }
        if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
          state.currentAdmin = { ...state.currentAdmin, accountStatus: 'active' };
        }
        if (state.user && (state.user._id === id || state.user.id === id)) {
          state.user = { ...state.user, accountStatus: 'active' };
        }
      })
      .addCase(adminActivateAdmin.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to activate admin';
        state.adminUpdateSuccess = false;
      })
      
      // ========== ADMIN GET ADMIN ACTIVITY ==========
      .addCase(adminGetAdminActivity.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(adminGetAdminActivity.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminActivity = action.payload.data || null;
      })
      .addCase(adminGetAdminActivity.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to fetch admin activity';
      })
      
      // ========== ADMIN CLEAR ADMIN ACTIVITY ==========
      .addCase(adminClearAdminActivity.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(adminClearAdminActivity.fulfilled, (state) => {
        state.adminLoading = false;
        state.adminActivity = null;
        state.adminUpdateSuccess = true;
      })
      .addCase(adminClearAdminActivity.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to clear admin activity';
      })
      
      // ========== ADMIN GET ADMIN ACTIVITY SUMMARY ==========
      .addCase(adminGetAdminActivitySummary.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(adminGetAdminActivitySummary.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminActivity = action.payload.data || null;
      })
      .addCase(adminGetAdminActivitySummary.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to fetch admin activity summary';
      })
      
      // ========== ADMIN GET ADMIN STATS SUMMARY ==========
      .addCase(adminGetAdminStatsSummary.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(adminGetAdminStatsSummary.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminStatsSummary = action.payload.data || null;
      })
      .addCase(adminGetAdminStatsSummary.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to fetch admin stats summary';
      })
      
      // ========== ADMIN GET ADMIN STATISTICS ==========
      .addCase(adminGetAdminStatistics.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(adminGetAdminStatistics.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminStats = action.payload.data || null;
      })
      .addCase(adminGetAdminStatistics.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to fetch admin statistics';
      })
      
      // ========== ADMIN UPDATE ADMIN STATS ==========
      .addCase(adminUpdateAdminStats.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
        state.adminUpdateSuccess = false;
      })
      .addCase(adminUpdateAdminStats.fulfilled, (state) => {
        state.adminLoading = false;
        state.adminUpdateSuccess = true;
      })
      .addCase(adminUpdateAdminStats.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload || 'Failed to update admin statistics';
        state.adminUpdateSuccess = false;
      }).addCase(adminUploadProfileImage.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminUploadProfileImage.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
  if (action.payload.data) {
    state.user = { ...state.user, ...action.payload.data };
    if (state.currentAdmin && state.currentAdmin._id === state.user?._id) {
      state.currentAdmin = { ...state.currentAdmin, ...action.payload.data };
    }
    const index = state.admins.findIndex(a => a._id === state.user?._id);
    if (index !== -1) {
      state.admins[index] = { ...state.admins[index], ...action.payload.data };
    }
  }
})
.addCase(adminUploadProfileImage.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to upload profile image';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN REMOVE PROFILE IMAGE ==========
.addCase(adminRemoveProfileImage.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminRemoveProfileImage.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
  if (action.payload.data) {
    state.user = { ...state.user, ...action.payload.data };
    if (state.currentAdmin && state.currentAdmin._id === state.user?._id) {
      state.currentAdmin = { ...state.currentAdmin, ...action.payload.data };
    }
    const index = state.admins.findIndex(a => a._id === state.user?._id);
    if (index !== -1) {
      state.admins[index] = { ...state.admins[index], ...action.payload.data };
    }
  }
})
.addCase(adminRemoveProfileImage.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to remove profile image';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN UPDATE BASIC INFO ==========
.addCase(adminUpdateBasicInfo.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminUpdateBasicInfo.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
  const { id, updateData } = action.payload;
  const index = state.admins.findIndex(a => a._id === id || a.id === id);
  if (index !== -1) {
    state.admins[index] = { ...state.admins[index], ...updateData };
  }
  if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
    state.currentAdmin = { ...state.currentAdmin, ...updateData };
  }
  if (state.user && (state.user._id === id || state.user.id === id)) {
    state.user = { ...state.user, ...updateData };
  }
})
.addCase(adminUpdateBasicInfo.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to update basic information';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN UPDATE EMAIL ==========
.addCase(adminUpdateEmail.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminUpdateEmail.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
  const { id, newEmail } = action.payload;
  const index = state.admins.findIndex(a => a._id === id || a.id === id);
  if (index !== -1) {
    state.admins[index] = { ...state.admins[index], email: newEmail, emailVerified: false };
  }
  if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
    state.currentAdmin = { ...state.currentAdmin, email: newEmail, emailVerified: false };
  }
  if (state.user && (state.user._id === id || state.user.id === id)) {
    state.user = { ...state.user, email: newEmail, emailVerified: false };
  }
})
.addCase(adminUpdateEmail.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to update email';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN UPDATE PASSWORD ==========
.addCase(adminUpdatePassword.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminUpdatePassword.fulfilled, (state) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
})
.addCase(adminUpdatePassword.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to update password';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN UPDATE FULL PROFILE ==========
.addCase(adminUpdateFullProfile.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminUpdateFullProfile.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
  const { id, updateData } = action.payload;
  const index = state.admins.findIndex(a => a._id === id || a.id === id);
  if (index !== -1) {
    state.admins[index] = { ...state.admins[index], ...updateData };
  }
  if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
    state.currentAdmin = { ...state.currentAdmin, ...updateData };
  }
  if (state.user && (state.user._id === id || state.user.id === id)) {
    state.user = { ...state.user, ...updateData };
  }
})
.addCase(adminUpdateFullProfile.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to update full profile';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN UPDATE TWO-FACTOR AUTH SETTINGS ==========
.addCase(adminUpdateTwoFactorAuth.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminUpdateTwoFactorAuth.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
  const { id, enabled } = action.payload;
  const index = state.admins.findIndex(a => a._id === id || a.id === id);
  if (index !== -1) {
    state.admins[index] = {
      ...state.admins[index],
      twoFactorAuth: { ...state.admins[index].twoFactorAuth, enabled }
    };
  }
  if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
    state.currentAdmin = {
      ...state.currentAdmin,
      twoFactorAuth: { ...state.currentAdmin.twoFactorAuth, enabled }
    };
  }
  if (state.user && (state.user._id === id || state.user.id === id)) {
    state.user = {
      ...state.user,
      twoFactorAuth: { ...state.user.twoFactorAuth, enabled }
    };
  }
})
.addCase(adminUpdateTwoFactorAuth.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to update two-factor authentication';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN UPDATE NOTIFICATION PREFERENCES ==========
.addCase(adminUpdateNotificationPreferences.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminUpdateNotificationPreferences.fulfilled, (state) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
})
.addCase(adminUpdateNotificationPreferences.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to update notification preferences';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN GET NOTIFICATION PREFERENCES ==========
.addCase(adminGetNotificationPreferences.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
})
.addCase(adminGetNotificationPreferences.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminStats = action.payload.data || null;
})
.addCase(adminGetNotificationPreferences.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to fetch notification preferences';
})

// ========== ADMIN UPDATE DASHBOARD SETTINGS ==========
.addCase(adminUpdateDashboardSettings.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminUpdateDashboardSettings.fulfilled, (state) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
})
.addCase(adminUpdateDashboardSettings.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to update dashboard settings';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN GET DASHBOARD SETTINGS ==========
.addCase(adminGetDashboardSettings.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
})
.addCase(adminGetDashboardSettings.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminStats = action.payload.data || null;
})
.addCase(adminGetDashboardSettings.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to fetch dashboard settings';
})

// ========== ADMIN TOGGLE TWO-FACTOR AUTH ==========
.addCase(adminToggleTwoFactorAuth.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminToggleTwoFactorAuth.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
  const { id, enabled } = action.payload;
  if (state.currentAdmin && (state.currentAdmin._id === id || state.currentAdmin.id === id)) {
    state.currentAdmin = {
      ...state.currentAdmin,
      twoFactorAuth: { ...state.currentAdmin.twoFactorAuth, enabled }
    };
  }
  if (state.user && (state.user._id === id || state.user.id === id)) {
    state.user = {
      ...state.user,
      twoFactorAuth: { ...state.user.twoFactorAuth, enabled }
    };
  }
})
.addCase(adminToggleTwoFactorAuth.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to toggle two-factor authentication';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN CHECK PERMISSION ==========
.addCase(adminCheckPermission.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
})
.addCase(adminCheckPermission.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.adminStats = action.payload.data || null;
})
.addCase(adminCheckPermission.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to check permission';
})

// ========== ADMIN GET PERMISSIONS ==========
.addCase(adminGetPermissions.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
})
.addCase(adminGetPermissions.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.permissions = action.payload.data || [];
})
.addCase(adminGetPermissions.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to fetch permissions';
})

// ========== ADMIN BULK UPDATE ADMINS ==========
.addCase(adminBulkUpdateAdmins.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminUpdateSuccess = false;
})
.addCase(adminBulkUpdateAdmins.fulfilled, (state) => {
  state.adminLoading = false;
  state.adminUpdateSuccess = true;
})
.addCase(adminBulkUpdateAdmins.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to bulk update admins';
  state.adminUpdateSuccess = false;
})

// ========== ADMIN BULK DELETE ADMINS ==========
.addCase(adminBulkDeleteAdmins.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
  state.adminDeleteSuccess = false;
})
.addCase(adminBulkDeleteAdmins.fulfilled, (state) => {
  state.adminLoading = false;
  state.adminDeleteSuccess = true;
  state.admins = [];
})
.addCase(adminBulkDeleteAdmins.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to bulk delete admins';
  state.adminDeleteSuccess = false;
})

// ========== ADMIN GET ROLES ==========
.addCase(adminGetRoles.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
})
.addCase(adminGetRoles.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.roles = action.payload.data || [];
})
.addCase(adminGetRoles.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to fetch admin roles';
})

// ========== ADMIN GET STATUSES ==========
.addCase(adminGetStatuses.pending, (state) => {
  state.adminLoading = true;
  state.adminError = null;
})
.addCase(adminGetStatuses.fulfilled, (state, action) => {
  state.adminLoading = false;
  state.statuses = action.payload.data || [];
})
.addCase(adminGetStatuses.rejected, (state, action) => {
  state.adminLoading = false;
  state.adminError = action.payload || 'Failed to fetch admin statuses';
})
  }
});

// ==================== SELECTORS ====================

const selectAdminState = (state) => state.admin;

// ==================== AUTH SELECTORS ====================

export const selectIsAuthenticated = createSelector(
  selectAdminState,
  (state) => state.isAuthenticated
);

export const selectCurrentUser = createSelector(
  selectAdminState,
  (state) => state.user
);

export const selectAuthLoading = createSelector(
  selectAdminState,
  (state) => state.authLoading
);

export const selectAuthError = createSelector(
  selectAdminState,
  (state) => state.authError
);

export const selectPasswordResetRequested = createSelector(
  selectAdminState,
  (state) => state.passwordResetRequested
);

export const selectPasswordResetSuccess = createSelector(
  selectAdminState,
  (state) => state.passwordResetSuccess
);

export const selectPasswordResetError = createSelector(
  selectAdminState,
  (state) => state.passwordResetError
);

export const selectEmailVerified = createSelector(
  selectAdminState,
  (state) => state.emailVerified
);

export const selectEmailVerificationError = createSelector(
  selectAdminState,
  (state) => state.emailVerificationError
);

// ==================== ADMIN MANAGEMENT SELECTORS ====================

export const selectAllAdmins = createSelector(
  selectAdminState,
  (state) => state.admins
);

export const selectCurrentAdmin = createSelector(
  selectAdminState,
  (state) => state.currentAdmin
);

export const selectAdminStats = createSelector(
  selectAdminState,
  (state) => state.adminStats
);

export const selectAdminActivity = createSelector(
  selectAdminState,
  (state) => state.adminActivity
);

export const selectAdminStatsSummary = createSelector(
  selectAdminState,
  (state) => state.adminStatsSummary
);

export const selectAdminLoading = createSelector(
  selectAdminState,
  (state) => state.adminLoading
);

export const selectAdminError = createSelector(
  selectAdminState,
  (state) => state.adminError
);

export const selectAdminDeleteSuccess = createSelector(
  selectAdminState,
  (state) => state.adminDeleteSuccess
);

export const selectAdminUpdateSuccess = createSelector(
  selectAdminState,
  (state) => state.adminUpdateSuccess
);

export const selectAdminPagination = createSelector(
  selectAdminState,
  (state) => state.pagination
);

export const selectAdminFilters = createSelector(
  selectAdminState,
  (state) => state.filters
);

export const selectAdminSearchQuery = createSelector(
  selectAdminState,
  (state) => state.searchQuery
);

export const selectAdminIsRefreshing = createSelector(
  selectAdminState,
  (state) => state.isRefreshing
);

export const selectSelectedAdminId = createSelector(
  selectAdminState,
  (state) => state.selectedAdminId
);

// ==================== PERMISSION SELECTORS ====================

export const selectAdminPermissions = createSelector(
  selectAdminState,
  (state) => state.permissions
);

export const selectAdminRoles = createSelector(
  selectAdminState,
  (state) => state.roles
);

export const selectAdminStatuses = createSelector(
  selectAdminState,
  (state) => state.statuses
);

// ==================== DERIVED SELECTORS ====================

export const selectFilteredAdmins = createSelector(
  [selectAllAdmins, selectAdminFilters, selectAdminSearchQuery],
  (admins, filters, searchQuery) => {
    let filtered = [...admins];
    
    // Apply filters
    if (filters.accountStatus) {
      filtered = filtered.filter(a => a.accountStatus === filters.accountStatus);
    }
    
    if (filters.role) {
      filtered = filtered.filter(a => a.role === filters.role);
    }
    
    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      filtered = filtered.filter(a => new Date(a.createdAt) >= dateFrom);
    }
    
    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      filtered = filtered.filter(a => new Date(a.createdAt) <= dateTo);
    }
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.name?.toLowerCase().includes(query) ||
        a.email?.toLowerCase().includes(query) ||
        a.phone?.toLowerCase().includes(query) ||
        a.role?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }
);

export const selectAdminById = (id) => createSelector(
  selectAllAdmins,
  (admins) => admins.find(a => a._id === id || a.id === id)
);

export const selectActiveAdmins = createSelector(
  selectAllAdmins,
  (admins) => admins.filter(a => a.accountStatus === 'active')
);

export const selectSuspendedAdmins = createSelector(
  selectAllAdmins,
  (admins) => admins.filter(a => a.accountStatus === 'suspended')
);

export const selectInactiveAdmins = createSelector(
  selectAllAdmins,
  (admins) => admins.filter(a => a.accountStatus === 'inactive')
);

export const selectAdminStatusCounts = createSelector(
  selectAllAdmins,
  (admins) => {
    const counts = {
      active: 0,
      suspended: 0,
      inactive: 0,
      total: admins.length
    };
    
    admins.forEach(a => {
      if (counts.hasOwnProperty(a.accountStatus)) {
        counts[a.accountStatus]++;
      }
    });
    
    return counts;
  }
);

// ==================== EXPORTS ====================

export const {
  resetAuthState,
  resetPasswordResetState,
  resetAdminState,
  clearErrors,
  resetUpdateState,
  resetDeleteState,
  setAdminFilters,
  clearAdminFilters,
  setAdminSearchQuery,
  setAdminPagination,
  setAdminRefreshing,
  selectAdmin,
  clearCurrentAdmin,
  updateAdminInList,
  removeAdminFromList,
  updateUserData,
  forceClearAdminData
} = adminSlice.actions;

export default adminSlice.reducer;